import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import personService from "services/person/personService";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config";
import { useNotification } from "context/NotificationContext";
import { parseObject } from "utils/helpers/dataHelper";

export default function usePerson(personId) {
    const isNew = !personId;

    const queryClient = useQueryClient();
    const cachedPersons = queryClient.getQueryData(["persons"]);

    const cachedPerson = cachedPersons?.find(
        (item) => item.id === Number(personId)
    );

    const [loading, setLoading] = useState(false);

    const [person, setPerson] = useState(null);

    const [form, setForm] = useState(null);

    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const navigate = useNavigate();
    const notify = useNotification();

    const isDirty =
        !!form && !!person && JSON.stringify(person) !== JSON.stringify(form);

    const load = async () => {
        if (isNew) {
            setPerson({});
            setForm({});
            return;
        }

        if (cachedPerson) {
            queryClient.setQueryData(["person", cachedPerson.id], cachedPerson);

            setPerson(cachedPerson);
            setForm(cachedPerson);

            return;
        }

        try {
            setLoading(true);

            const { data } = await personService.get(personId);

            setPerson(data);
            setForm(data);
        } catch (err) {
            console.error("Error fetching person data:", {
                err,
                status: err?.response?.status
            });
            setNotFound(true);
            let errMsg = "Failed to load employee data.";
            if (err.response) {
                // Server responded with status code outside 2xx (e.g., 403, 401, 500)
                if (err.response.status === 403) {
                    errMsg =
                        "Access Denied. You dont have access to this page.";
                    setError(errMsg);
                } else if (err.response.status === 500) {
                    errMsg = "person not found";
                    console.log(errMsg, err);
                } else {
                    errMsg = "Failed to fetch data from the server.";
                    setError(errMsg);
                }
                notify.success(errMsg);
            } else if (err.request) {
                // Backend is down or network issue (no response received)
                setError(
                    "Unable to reach the server. Please check your connection."
                );
                notify.success(errMsg);
            } else {
                setError("An unexpected error occurred.");
                notify.success("An unexpected error occurred.");
            }
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        /* if (isNew) {
             return;
         }
 */

        try {
            setLoading(true);
            const parseData = parseObject(person, form, { includeMeta: false });
            // return;
            /*const { data: savedData } = isNew
                ? await personService.create(form)
                : await personService.update(
                      person.id,
                      parseObject(person, form, { includeMeta: false })
                  );

            const { data } = await personService.get(savedData?.id);

            setForm(data);
            setPerson(data);*/
            const { data: savedData } = isNew
                ? await personService.create(form)
                : await personService.update(
                      person.id,
                      parseObject(person, form, { includeMeta: false })
                  );

            // we can removed later when my backend is ready for the result
            const { data } = await personService.get(savedData?.id);

            const savedPerson = data;

            setForm(savedPerson);
            setPerson(savedPerson);

            if (savedPerson?.id) {
                queryClient.setQueryData(
                    ["person", savedPerson.id],
                    savedPerson
                );

                queryClient.setQueryData(["persons"], (currentItems) => {
                    if (!currentItems) {
                        return currentItems;
                    }

                    if (isNew) {
                        return [...currentItems, savedPerson];
                    }

                    return currentItems.map((item) =>
                        item.id === savedPerson.id ? savedPerson : item
                    );
                });
            }

            notify.success(`${isNew ? "New" : "Updated"} employee saved!`);
            if (isNew && savedData?.id) {
                // console.log("navigate to employees", { data });
                setTimeout(() => {
                    navigate(ROUTES.EMPLOYEE(savedData?.id), { replace: true });
                }, 500);
            }
        } catch (err) {
            if (err.response) {
                // Server responded with status code outside 2xx (e.g., 403, 401, 500)
                if (err.response.status === 403) {
                    setError(
                        "Your dont have access to this page. Please contact your administrator."
                    );
                } else {
                    setError("Failed to fetch data from the server.");
                }
            } else if (err.request) {
                // Backend is down or network issue (no response received)
                setError(
                    "Unable to reach the server. Please check your connection."
                );
            } else {
                setError("An unexpected error occurred.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setNotFound(false);
        load();
    }, [personId]);

    const activeEmployee = useMemo(() => {
        return (
            form?.employees?.find(
                (employee) => employee.employmentStatus === "Active"
            ) ?? null
        );
    }, [form]);

    const updateField = (field, value) => {
        setError(null);
        setForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    return {
        loading,
        person,
        form,
        activeEmployee,
        updateField,
        save,
        error,
        isDirty,
        notFound
    };
}
